import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "motion/react";
import {
    AppBar,
    Toolbar,
    IconButton,
    Drawer,
    Box,
    Stack,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";

import { updateController } from "../../reducx/features/controllerRedux";
import ClientCalling from "../../api/ClientCalling";
import logo from "../../Image/Client/logo1.png";
import CookieConsent from "../Client/CookieConsent";
import getFcmToken from "../firebase/getFcmToken";
import FixedDownload from "../Client/FixedDownload";
import appstore from "../../Image/Client/appStore.svg";
import playstore from "../../Image/Client/playstore.svg";
import insta from "../../Image/Client/insta.svg";
import fb from "../../Image/Client/fb.svg";
import thr from "../../Image/Client/thr.svg";
import { HiSparkles } from "react-icons/hi2";
import { FiArrowRight, FiHome, FiInfo, FiFileText, FiShield, FiHelpCircle, FiMail, FiBookOpen, FiImage, FiZap } from "react-icons/fi";

const NAV_LINKS = [
    { label: "Status Maker", path: "/inpix", icon: FiZap },
    { label: "Game", path: "/quizzy/", icon: FiZap, external: true },
    { label: "Wallpapers", path: "/wallpaper", icon: FiImage },
    { label: "About Us", path: "/about", icon: FiInfo },
    { label: "Blogs", path: "/blog", icon: FiBookOpen },
    { label: "Privacy Policy", path: "/privacy-policy", icon: FiShield },
    { label: "Terms & Conditions", path: "/terms", icon: FiFileText },
    { label: "FAQs", path: "/faqs", icon: FiHelpCircle },
    { label: "Contact Us", path: "/contact", icon: FiMail },
];

const SOCIALS = [
    { icon: insta, alt: "Instagram", url: "https://www.instagram.com/picpromptify/" },
    { icon: fb, alt: "Facebook", url: "https://www.facebook.com/profile.php?id=61579052481615" },
    { icon: thr, alt: "Threads", url: "https://www.threads.com/@picpromptify" },
];

const COLORS = {
    primary: "#7F00FF",
    title: "#FCFA02",
    font: "#FFFFFF",
    back: "#121212",
    secondary: "#1A1A1A",
};

const ClientNavbar = () => {
    const controller = useSelector((state) => state.controller.controller);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    const [loader, setLoader] = useState(false);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        getFcmToken();
    }, []);

    useEffect(() => {
        const fetchControllerData = async () => {
            try {
                setLoader(true);
                const res = await ClientCalling?.getControlleClient();
                if (!res?.data || Object.keys(res.data).length === 0) {
                    navigate("/error");
                    return;
                }
                dispatch(updateController(res.data));
            } catch (err) {
                console.error("Failed to fetch controller:", err);
                navigate("/error");
            } finally {
                setLoader(false);
            }
        };
        fetchControllerData();
    }, [dispatch, navigate]);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 30);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        document.body.style.overflow = drawerOpen ? "hidden" : "unset";
        return () => {
            document.body.style.overflow = "unset";
        };
    }, [drawerOpen]);

    const handleNavigate = (path) => {
        navigate(path);
        setDrawerOpen(false);
    };

    if (loader || !controller) return null;

    return (
        <>
            <CookieConsent />
            <FixedDownload />

            <Box
                sx={{
                    position: "relative",
                    zIndex: 30,
                    mt: 2,
                    py: 1,
                    width: "100%",
                    px: { xs: 1.5, sm: 2.5, md: 4, lg: 6 },
                    boxSizing: "border-box",
                }}
            >
                <motion.div
                    initial={{ y: -40, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ type: "spring", stiffness: 130, damping: 22 }}
                >
                    <AppBar
                        position="static"
                        elevation={0}
                        sx={{
                            background: scrolled
                                ? "rgba(15,15,18,0.7)"
                                : "rgba(255,255,255,0.08)",
                            backdropFilter: "blur(28px) saturate(180%)",
                            WebkitBackdropFilter: "blur(28px) saturate(180%)",
                            border: "1px solid rgba(255,255,255,0.16)",
                            borderRadius: 3.5,
                            boxShadow: scrolled
                                ? "0 14px 40px -12px rgba(127,0,255,0.4), inset 0 1px 0 0 rgba(255,255,255,0.18), inset 0 -1px 0 0 rgba(255,255,255,0.04)"
                                : "0 8px 32px rgba(0,0,0,0.35), inset 0 1px 0 0 rgba(255,255,255,0.18), inset 0 -1px 0 0 rgba(255,255,255,0.04)",
                            transition: "background 0.4s ease, box-shadow 0.4s ease",
                            color: COLORS.font,
                        }}
                    >
                        <Toolbar
                            disableGutters
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                width: "100%",
                                px: { xs: 2, sm: 2.5, md: 3 },
                                minHeight: { xs: 60, md: 68 },
                                gap: 2,
                            }}
                        >
                            {/* Logo */}
                            <motion.div
                                whileHover={{
                                    scale: 1.06,
                                    filter: "drop-shadow(0 0 12px rgba(127,0,255,0.55))",
                                }}
                                whileTap={{ scale: 0.95 }}
                                transition={{ type: "spring", stiffness: 320, damping: 18 }}
                                style={{ cursor: "pointer", display: "flex", alignItems: "center" }}
                                onClick={() => navigate("/")}
                            >
                                <img
                                    src={logo}
                                    alt="PicPromptify"
                                    style={{ height: 36, width: "auto", display: "block" }}
                                />
                            </motion.div>

                            {/* Desktop links */}
                            <Stack
                                direction="row"
                                spacing={3.5}
                                sx={{
                                    display: { xs: "none", md: "flex" },
                                    alignItems: "center",
                                }}
                            >
                                {NAV_LINKS.map((item) => {
                                    const active = location.pathname === item.path;
                                    return (
                                        <Box
                                            key={item.path}
                                            onClick={() => (item.external ? (window.location.href = item.path) : navigate(item.path))}
                                            sx={{
                                                position: "relative",
                                                cursor: "pointer",
                                                color: active ? COLORS.title : COLORS.font,
                                                fontSize: 15,
                                                fontWeight: 500,
                                                letterSpacing: 0.3,
                                                py: 0.5,
                                                transition: "color 0.25s ease",
                                                "&:hover": { color: COLORS.title },
                                            }}
                                        >
                                            {item.label}
                                            {active && (
                                                <motion.span
                                                    layoutId="nav-underline"
                                                    style={{
                                                        position: "absolute",
                                                        left: 0,
                                                        right: 0,
                                                        bottom: -6,
                                                        height: 2,
                                                        borderRadius: 2,
                                                        background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.title})`,
                                                    }}
                                                    transition={{
                                                        type: "spring",
                                                        stiffness: 280,
                                                        damping: 28,
                                                    }}
                                                />
                                            )}
                                        </Box>
                                    );
                                })}
                            </Stack>

                            {/* Mobile hamburger */}
                            <IconButton
                                onClick={() => setDrawerOpen(true)}
                                aria-label="Open menu"
                                sx={{
                                    display: { xs: "inline-flex", md: "none" },
                                    color: COLORS.font,
                                    p: 1,
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <motion.div
                                    style={{ display: "inline-flex", alignItems: "center" }}
                                    animate={{ rotate: drawerOpen ? 90 : 0 }}
                                    transition={{ type: "spring", stiffness: 280, damping: 22 }}
                                >
                                    <MenuIcon sx={{ fontSize: 28 }} />
                                </motion.div>
                            </IconButton>
                        </Toolbar>
                    </AppBar>
                </motion.div>
            </Box>

            {/* Mobile + Tablet Drawer */}
            <Drawer
                anchor="right"
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                sx={{
                    "& .MuiDrawer-paper": {
                        width: { xs: "85vw", sm: 360 },
                        maxWidth: 400,
                        backgroundColor: "#0a0810",
                        backgroundImage:
                            "linear-gradient(165deg, #1c1230 0%, #0a0810 60%, #06040a 100%)",
                        borderLeft: "1px solid rgba(255,255,255,0.14)",
                        boxShadow:
                            "-30px 0 60px -20px rgba(127,0,255,0.4), -20px 0 50px -20px rgba(0,0,0,0.7), inset 1px 0 0 0 rgba(255,255,255,0.12)",
                        color: COLORS.font,
                        overflowX: "hidden",
                    },
                    "& .MuiBackdrop-root": {
                        backgroundColor: "rgba(0,0,0,0.55)",
                        backdropFilter: "blur(4px)",
                        WebkitBackdropFilter: "blur(4px)",
                    },
                }}
                transitionDuration={400}
            >
                {/* Decorative orbs — static (animated blur is a heavy per-frame repaint on mobile) */}
                <Box
                    aria-hidden
                    sx={{
                        position: "absolute",
                        top: 40,
                        right: -60,
                        width: 220,
                        height: 220,
                        borderRadius: "50%",
                        opacity: 0.35,
                        filter: "blur(60px)",
                        background:
                            "radial-gradient(circle, #7F00FF 0%, transparent 70%)",
                        pointerEvents: "none",
                    }}
                />
                <Box
                    aria-hidden
                    sx={{
                        position: "absolute",
                        bottom: 80,
                        left: -60,
                        width: 200,
                        height: 200,
                        borderRadius: "50%",
                        opacity: 0.25,
                        filter: "blur(60px)",
                        background:
                            "radial-gradient(circle, #FCFA02 0%, transparent 70%)",
                        pointerEvents: "none",
                    }}
                />

                {/* Header */}
                <Box
                    sx={{
                        position: "relative",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        px: 3,
                        pt: 3,
                        pb: 2.5,
                        borderBottom: "1px solid rgba(255,255,255,0.08)",
                    }}
                >
                    <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.1, duration: 0.4 }}
                        style={{ display: "flex", alignItems: "center", gap: 10 }}
                    >
                        <img
                            src={logo}
                            alt="PicPromptify"
                            style={{ height: 32, width: "auto" }}
                        />
                    </motion.div>
                    <IconButton
                        onClick={() => setDrawerOpen(false)}
                        aria-label="Close menu"
                        sx={{
                            color: COLORS.font,
                            background: "rgba(255,255,255,0.05)",
                            border: "1px solid rgba(255,255,255,0.12)",
                            width: 38,
                            height: 38,
                            "&:hover": {
                                background: "rgba(127,0,255,0.2)",
                            },
                        }}
                    >
                        <motion.div
                            whileHover={{ rotate: 90 }}
                            whileTap={{ scale: 0.85 }}
                            transition={{ type: "spring", stiffness: 320, damping: 18 }}
                            style={{ display: "inline-flex" }}
                        >
                            <CloseIcon sx={{ fontSize: 22 }} />
                        </motion.div>
                    </IconButton>
                </Box>

                <AnimatePresence>
                    {drawerOpen && (
                        <motion.div
                            initial="hidden"
                            animate="visible"
                            exit="hidden"
                            variants={{
                                visible: {
                                    transition: {
                                        staggerChildren: 0.05,
                                        delayChildren: 0.15,
                                    },
                                },
                                hidden: {},
                            }}
                            style={{ position: "relative" }}
                        >
                            {/* Eyebrow */}
                            <motion.div
                                variants={{
                                    hidden: { opacity: 0, y: -10 },
                                    visible: { opacity: 1, y: 0 },
                                }}
                                style={{
                                    padding: "16px 24px 8px",
                                }}
                            >
                                <Box
                                    sx={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: 0.8,
                                        px: 1.5,
                                        py: 0.5,
                                        borderRadius: 999,
                                        background: "rgba(255,255,255,0.05)",
                                        border: "1px solid rgba(255,255,255,0.1)",
                                        fontSize: 10,
                                        letterSpacing: "0.18em",
                                        textTransform: "uppercase",
                                        color: "rgba(255,255,255,0.7)",
                                    }}
                                >
                                    <HiSparkles style={{ color: COLORS.title, fontSize: 12 }} />
                                    Navigation
                                </Box>
                            </motion.div>

                            <Stack spacing={1} sx={{ px: 2.5, pb: 2 }}>
                                {NAV_LINKS.map((item) => {
                                    const active = location.pathname === item.path;
                                    const Icon = item.icon;
                                    return (
                                        <motion.div
                                            key={item.path}
                                            variants={{
                                                hidden: { x: 60, opacity: 0 },
                                                visible: { x: 0, opacity: 1 },
                                            }}
                                            whileHover={{ x: 6 }}
                                            whileTap={{ scale: 0.97 }}
                                            transition={{
                                                type: "spring",
                                                stiffness: 260,
                                                damping: 22,
                                            }}
                                        >
                                            <Box
                                                onClick={() => (item.external ? (window.location.href = item.path) : handleNavigate(item.path))}
                                                sx={{
                                                    position: "relative",
                                                    cursor: "pointer",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 1.5,
                                                    px: 2,
                                                    py: 1.5,
                                                    borderRadius: 2.5,
                                                    background: active
                                                        ? "linear-gradient(135deg, rgba(127,0,255,0.25), rgba(252,250,2,0.08))"
                                                        : "rgba(255,255,255,0.025)",
                                                    border: active
                                                        ? "1px solid rgba(127,0,255,0.4)"
                                                        : "1px solid rgba(255,255,255,0.06)",
                                                    color: active ? COLORS.title : COLORS.font,
                                                    fontSize: 15,
                                                    fontWeight: active ? 600 : 500,
                                                    transition:
                                                        "background 0.3s, border 0.3s, color 0.25s",
                                                    "&:hover": {
                                                        background:
                                                            "linear-gradient(135deg, rgba(127,0,255,0.18), rgba(255,255,255,0.04))",
                                                        border: "1px solid rgba(255,255,255,0.14)",
                                                        color: COLORS.title,
                                                    },
                                                    overflow: "hidden",
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        width: 36,
                                                        height: 36,
                                                        borderRadius: 1.5,
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        background: active
                                                            ? "linear-gradient(135deg, #7F00FF, #FF8AFF)"
                                                            : "rgba(255,255,255,0.05)",
                                                        border: active
                                                            ? "1px solid rgba(255,255,255,0.18)"
                                                            : "1px solid rgba(255,255,255,0.08)",
                                                        flexShrink: 0,
                                                        boxShadow: active
                                                            ? "0 6px 16px -4px rgba(127,0,255,0.5)"
                                                            : "none",
                                                    }}
                                                >
                                                    <Icon
                                                        style={{
                                                            fontSize: 16,
                                                            color: active ? "#fff" : COLORS.font,
                                                        }}
                                                    />
                                                </Box>
                                                <Box sx={{ flex: 1, minWidth: 0 }}>{item.label}</Box>
                                                {active && (
                                                    <motion.div
                                                        initial={{ opacity: 0, x: -6 }}
                                                        animate={{ opacity: 1, x: 0 }}
                                                        style={{ display: "inline-flex" }}
                                                    >
                                                        <FiArrowRight
                                                            style={{ color: COLORS.title, fontSize: 16 }}
                                                        />
                                                    </motion.div>
                                                )}
                                            </Box>
                                        </motion.div>
                                    );
                                })}
                            </Stack>

                            {/* Divider */}
                            <Box sx={{ px: 3, my: 1 }}>
                                <Box
                                    sx={{
                                        height: 1,
                                        background:
                                            "linear-gradient(90deg, transparent, rgba(255,255,255,0.18), transparent)",
                                    }}
                                />
                            </Box>

                            {/* Download section */}
                            <motion.div
                                variants={{
                                    hidden: { y: 30, opacity: 0 },
                                    visible: { y: 0, opacity: 1 },
                                }}
                                transition={{ type: "spring", stiffness: 220, damping: 24 }}
                                style={{ padding: "12px 24px 16px" }}
                            >
                                <Box
                                    sx={{
                                        fontSize: 10,
                                        letterSpacing: "0.2em",
                                        textTransform: "uppercase",
                                        color: "rgba(255,255,255,0.5)",
                                        mb: 1.5,
                                        fontWeight: 600,
                                    }}
                                >
                                    Get the App
                                </Box>
                                <Stack direction="row" spacing={1.2}>
                                    <motion.button
                                        whileHover={{ y: -2, scale: 1.03 }}
                                        whileTap={{ scale: 0.96 }}
                                        onClick={() =>
                                            window.open(
                                                "https://apps.apple.com/in/app/picpromptify/id6749353177",
                                                "_blank"
                                            )
                                        }
                                        style={{
                                            flex: 1,
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 8,
                                            padding: "8px 10px",
                                            borderRadius: 12,
                                            background: "rgba(255,255,255,0.06)",
                                            border: "1px solid rgba(255,255,255,0.12)",
                                            backdropFilter: "blur(12px)",
                                            cursor: "pointer",
                                            color: "#fff",
                                        }}
                                    >
                                        <img
                                            src={appstore}
                                            alt="App Store"
                                            style={{ width: 22, height: 22, objectFit: "contain" }}
                                        />
                                        <div
                                            style={{
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "flex-start",
                                                lineHeight: 1.1,
                                                textAlign: "left",
                                            }}
                                        >
                                            <span
                                                style={{
                                                    fontSize: 8,
                                                    opacity: 0.6,
                                                    textTransform: "uppercase",
                                                    letterSpacing: 0.5,
                                                }}
                                            >
                                                Download
                                            </span>
                                            <span style={{ fontSize: 12, fontWeight: 600 }}>
                                                App Store
                                            </span>
                                        </div>
                                    </motion.button>
                                    <motion.button
                                        whileHover={{ y: -2, scale: 1.03 }}
                                        whileTap={{ scale: 0.96 }}
                                        onClick={() =>
                                            window.open(
                                                "https://play.google.com/store/apps/details?id=com.picprompter.pixprompt.prompt.ly.android",
                                                "_blank"
                                            )
                                        }
                                        style={{
                                            flex: 1,
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 8,
                                            padding: "8px 10px",
                                            borderRadius: 12,
                                            background: "rgba(255,255,255,0.06)",
                                            border: "1px solid rgba(255,255,255,0.12)",
                                            backdropFilter: "blur(12px)",
                                            cursor: "pointer",
                                            color: "#fff",
                                        }}
                                    >
                                        <img
                                            src={playstore}
                                            alt="Play Store"
                                            style={{ width: 22, height: 22, objectFit: "contain" }}
                                        />
                                        <div
                                            style={{
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "flex-start",
                                                lineHeight: 1.1,
                                                textAlign: "left",
                                            }}
                                        >
                                            <span
                                                style={{
                                                    fontSize: 8,
                                                    opacity: 0.6,
                                                    textTransform: "uppercase",
                                                    letterSpacing: 0.5,
                                                }}
                                            >
                                                Get on
                                            </span>
                                            <span style={{ fontSize: 12, fontWeight: 600 }}>
                                                Google Play
                                            </span>
                                        </div>
                                    </motion.button>
                                </Stack>
                            </motion.div>

                            {/* Socials */}
                            <motion.div
                                variants={{
                                    hidden: { y: 30, opacity: 0 },
                                    visible: { y: 0, opacity: 1 },
                                }}
                                transition={{ type: "spring", stiffness: 220, damping: 24 }}
                                style={{ padding: "8px 24px 24px" }}
                            >
                                <Box
                                    sx={{
                                        fontSize: 10,
                                        letterSpacing: "0.2em",
                                        textTransform: "uppercase",
                                        color: "rgba(255,255,255,0.5)",
                                        mb: 1.5,
                                        fontWeight: 600,
                                    }}
                                >
                                    Follow Us
                                </Box>
                                <Stack direction="row" spacing={1.2}>
                                    {SOCIALS.map((s, i) => {
                                        const brand =
                                            i === 0
                                                ? "linear-gradient(135deg, #F58529, #DD2A7B, #8134AF)"
                                                : i === 1
                                                    ? "linear-gradient(135deg, #1877F2, #4267B2)"
                                                    : "linear-gradient(135deg, #000000, #555555)";
                                        return (
                                            <motion.button
                                                key={s.alt}
                                                whileHover={{ y: -3, scale: 1.08 }}
                                                whileTap={{ scale: 0.9 }}
                                                onClick={() => window.open(s.url, "_blank")}
                                                aria-label={s.alt}
                                                style={{
                                                    position: "relative",
                                                    width: 42,
                                                    height: 42,
                                                    borderRadius: 12,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    background: "rgba(255,255,255,0.06)",
                                                    border: "1px solid rgba(255,255,255,0.12)",
                                                    backdropFilter: "blur(12px)",
                                                    cursor: "pointer",
                                                    overflow: "hidden",
                                                }}
                                            >
                                                <motion.div
                                                    initial={{ opacity: 0 }}
                                                    whileHover={{ opacity: 1 }}
                                                    transition={{ duration: 0.3 }}
                                                    style={{
                                                        position: "absolute",
                                                        inset: 0,
                                                        background: brand,
                                                    }}
                                                />
                                                <img
                                                    src={s.icon}
                                                    alt={s.alt}
                                                    style={{
                                                        position: "relative",
                                                        width: 16,
                                                        height: 16,
                                                    }}
                                                />
                                            </motion.button>
                                        );
                                    })}
                                </Stack>
                            </motion.div>

                            {/* Footer note */}
                            <motion.div
                                variants={{
                                    hidden: { opacity: 0 },
                                    visible: { opacity: 1 },
                                }}
                                style={{
                                    padding: "12px 24px 32px",
                                    fontSize: 10,
                                    color: "rgba(255,255,255,0.4)",
                                    textAlign: "center",
                                    letterSpacing: 0.4,
                                }}
                            >
                                © {new Date().getFullYear()} PicPromptify
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </Drawer>
        </>
    );
};

export default ClientNavbar;
